import { useEffect, useState, useCallback, useMemo } from 'react';
import CollapsibleTable from '../../components/table';
import UpdateIcon from '@mui/icons-material/Update';
import axios from 'axios';
import Swal from 'sweetalert2';
import config from '../../config/config';
import handleOpenSweetAlert from '../../components/handleOpenSweetAlert';
import SimpleSuccessSpinner from '../../components/LoadingSpinner';
import ModalConfirmarPrecios from '../../components/ModalConfirmarPrecios';
import { useSession } from '../../components/hooks/useSession';
import ExportButton from '../../components/BotonEx';
import { Box } from '@mui/material';

// Normaliza idEmpresa que puede venir como:
//  - [2]
//  - "2"
//  - "[1,2,3]"
const normalizeEmpresaArray = (idEmpresa) => {
  if (idEmpresa == null) return [];

  if (Array.isArray(idEmpresa)) {
    return idEmpresa.map((x) => Number(x)).filter((n) => Number.isFinite(n) && n > 0);
  }

  if (typeof idEmpresa === 'string') {
    const s = idEmpresa.trim();

    if (s.startsWith('[') && s.endsWith(']')) {
      try {
        const arr = JSON.parse(s);
        if (Array.isArray(arr)) {
          return arr.map((x) => Number(x)).filter((n) => Number.isFinite(n) && n > 0);
        }
      } catch {}
    }

    const n = Number(s);
    return Number.isFinite(n) && n > 0 ? [n] : [];
  }

  const n = Number(idEmpresa);
  return Number.isFinite(n) && n > 0 ? [n] : [];
};

export default function Tesorero({ idPerfil, onTotalChange }) {
  const apiUrl = config.apiBaseUrl;
  const { loggedInUsername, idEmpresa, nombreEmpresa } = useSession();

  // UsuarioId para persistencia
  const usuarioId = useMemo(() => Number(sessionStorage.getItem('UsuarioId') || 0), []);

  // Key estable
  const empresaIdsKey = useMemo(() => {
    const arr = normalizeEmpresaArray(idEmpresa);
    return arr.length ? arr.join(',') : '';
  }, [idEmpresa]);

  const empresaIds = useMemo(() => {
    if (!empresaIdsKey) return [];
    return empresaIdsKey
      .split(',')
      .map((n) => Number(n))
      .filter((x) => Number.isFinite(x) && x > 0);
  }, [empresaIdsKey]);

  // Para preferencias: TODAS => 0
  const empresaIdPref = useMemo(() => {
    return empresaIds.length > 1 ? 0 : (empresaIds[0] ?? 0);
  }, [empresaIds]);

  const [data, setData] = useState({ contenido: [] });
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState({});

  const fetchData = useCallback(async () => {
    if (!apiUrl) return;
    if (!empresaIdsKey) return;

    try {
      const response = await axios.post(`${apiUrl}/confirmacionPreciosTabla`, {
        idEmpresa: empresaIds,
        nombreEmpresa,
        loggedInUsername,
        usuarioId: usuarioId, // ENVIAMOS EL ID REAL DEL USUARIO
        idPerfil: idPerfil
      });
      setData(response.data);
      if (onTotalChange) {
        onTotalChange(response.data.total || response.data.contenido?.length || 0);
      }
    } catch (err) {
      console.error('Error al cargar datos:', err);
    }
  }, [apiUrl, empresaIds, empresaIdsKey, nombreEmpresa, loggedInUsername, usuarioId, idPerfil, onTotalChange]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleActualizarPrecios = useCallback(async (row) => {
    const { MONTOSOLICITADO, TOTAL, SOLICITANTE, ID, FOLIO } = row;

    if (SOLICITANTE !== loggedInUsername) {
      return handleOpenSweetAlert('Solo el solicitante puede actualizar precios.', 'warning');
    }

    if (MONTOSOLICITADO === TOTAL) {
      await Swal.fire({
        title: 'Solo actualizar fecha',
        text: 'El monto solicitado es igual al total. Solo puedes actualizar la fecha de vencimiento.',
        icon: 'info',
        confirmButtonText: 'Continuar'
      });
    }

    try {
      const empresaParam = empresaIds.length ? empresaIds.join(',') : '';
      const res = await axios.get(
        `${apiUrl}/confirmacionPreciosDatosActualizar/${ID}/${FOLIO}/${empresaParam}`
      );
      setSelectedRowData(res.data[0][0]);
      setOpenModal(true);
    } catch (err) {
      const msg = err.response?.data?.error || 'Error al consultar los datos';
      console.error('Error:', msg);
      handleOpenSweetAlert(msg, 'error');
    }
  }, [loggedInUsername, empresaIds, apiUrl]);

  const buttons = useMemo(() => [
    {
      id: 1,
      label: 'Actualizar',
      onClick: handleActualizarPrecios,
      icon: <UpdateIcon />,
      buttonColor: '#FF8C00',
      buttonFontColor: '#FFFFFF',
      isVisible: true,
    }
  ], [handleActualizarPrecios]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <Box sx={{ flexGrow: 1, overflow: 'hidden', minHeight: 0 }}>
        <CollapsibleTable
        dataTable={data}
        buttons={buttons}
        enableFiltering={false}
        showExportButton
        rightButtons={<ExportButton data={data.contenido} />}
        mobileCard={{ statusMode: 'none' }}
        columnLabels={{
          FECHA: 'Orden',
          FECHASOLICITUD: 'Fecha Solicitud',
          NOTAS: 'Notas Requi',
          NUMEROCC: 'Numero Centro Costo',
          CENTROCOSTO: 'Nombre CC',
          DEPARTAMENTO: 'Departamento',
          FOLIO_FACTURA: 'Factura',
          NUMERO_ECONOMICO: 'Economico',
          TIPO_PAGO: 'Condicion Pago',
          NOTA: 'Nota Requi',
          METODOPAGO: 'Metodo Pago',
        }}

        persistEnabled
        persistKey="ConfirmacionPrecios"
        persistVista="layout"
        usuarioId={usuarioId}
        empresaId={empresaIdPref}
        apiUrl={apiUrl}
      />
      </Box>

      <SimpleSuccessSpinner
        open={loading}
        handleClose={() => setLoading(false)}
      />

      <ModalConfirmarPrecios
        open={openModal}
        handleClose={() => setOpenModal(false)}
        data={selectedRowData}
        handleDataUpdate={fetchData}
      />
    </Box>
  );
}